import { useEffect, useState } from "react";
import {
  beginHistoryInteraction,
  endHistoryInteraction,
} from "../../../domain/document-lifecycle";

export const EMAIL_BUILDER_CONTINUOUS_HISTORY_IDLE_MS = 500;

type TEmailBuilderContinuousHistoryGroupActive = {
  controlKey: string;
  historyGroupId: string;
  gestureHeld: boolean;
  idleTimer: ReturnType<typeof setTimeout> | null;
};

type TEmailBuilderContinuousHistoryGroupDeps = {
  createId: () => string;
  setTimeout: typeof setTimeout;
  clearTimeout: typeof clearTimeout;
  idleMs: number;
};

const defaultDeps: TEmailBuilderContinuousHistoryGroupDeps = {
  createId: () => crypto.randomUUID(),
  setTimeout: ((handler: () => void, timeout?: number) =>
    globalThis.setTimeout(handler, timeout)) as typeof setTimeout,
  clearTimeout: ((timeoutId: ReturnType<typeof setTimeout>) =>
    globalThis.clearTimeout(timeoutId)) as typeof clearTimeout,
  idleMs: EMAIL_BUILDER_CONTINUOUS_HISTORY_IDLE_MS,
};

const deps: TEmailBuilderContinuousHistoryGroupDeps = defaultDeps;
let active: TEmailBuilderContinuousHistoryGroupActive | null = null;

function clearIdleTimer(): void {
  if (active?.idleTimer != null) {
    deps.clearTimeout(active.idleTimer);
    active.idleTimer = null;
  }
}

function clearActive(): void {
  clearIdleTimer();
  if (active?.gestureHeld) {
    endHistoryInteraction();
  }
  active = null;
}

function scheduleIdleEnd(controlKey: string): void {
  if (active === null || active.controlKey !== controlKey || active.gestureHeld) {
    return;
  }

  clearIdleTimer();
  const scheduledFor = controlKey;
  active.idleTimer = deps.setTimeout(() => {
    if (active?.controlKey === scheduledFor && !active.gestureHeld) {
      clearActive();
    }
  }, deps.idleMs);
}

function ensureGroup(controlKey: string, gestureHeld: boolean): string {
  if (active !== null && active.controlKey === controlKey) {
    if (gestureHeld) {
      if (!active.gestureHeld) {
        beginHistoryInteraction();
      }
      active.gestureHeld = true;
      clearIdleTimer();
    } else if (!active.gestureHeld) {
      scheduleIdleEnd(controlKey);
    }
    return active.historyGroupId;
  }

  clearActive();
  const historyGroupId = deps.createId();
  active = {
    controlKey,
    historyGroupId,
    gestureHeld,
    idleTimer: null,
  };
  if (gestureHeld) {
    beginHistoryInteraction();
  }
  if (!gestureHeld) {
    scheduleIdleEnd(controlKey);
  }
  return historyGroupId;
}

export function beginEmailBuilderContinuousHistoryGroup(controlKey: string): string {
  return ensureGroup(controlKey, true);
}

export function noteEmailBuilderContinuousHistoryGroup(controlKey: string): string {
  if (active !== null && active.controlKey === controlKey) {
    if (!active.gestureHeld) {
      scheduleIdleEnd(controlKey);
    }
    return active.historyGroupId;
  }

  return ensureGroup(controlKey, false);
}

export function endEmailBuilderContinuousHistoryGroup(controlKey: string): void {
  if (active?.controlKey !== controlKey) {
    return;
  }

  clearActive();
}

export function useEmailBuilderContinuousHistoryGroup(): {
  begin: () => string;
  noteChange: () => string;
  end: () => void;
} {
  const [controlKey] = useState(() => crypto.randomUUID());

  useEffect(() => {
    return () => {
      endEmailBuilderContinuousHistoryGroup(controlKey);
    };
  }, [controlKey]);

  return {
    begin: () => beginEmailBuilderContinuousHistoryGroup(controlKey),
    noteChange: () => noteEmailBuilderContinuousHistoryGroup(controlKey),
    end: () => endEmailBuilderContinuousHistoryGroup(controlKey),
  };
}
